import React from 'react';
import { format, isSameMinute } from 'date-fns';
import { Trans, useTranslation } from 'react-i18next';

import { OmaLummeApp } from '../../omaLummeContext';

import styles from './styles/LandingPageLifts.module.scss';

import Liftup from '../Common/Liftup';
import PriceFixLift from './LandingPageLifts/PriceFixLift';
import EVChargerLift from './LandingPageLifts/EVChargerLift';
import ConsumptionLift from './LandingPageLifts/ConsumptionLift';

// Types.
import { InvoiceAkamon } from '../../types/billing';

// Utils.
import { setPriceFormat, roundNumber, formatDate } from '../../utils/billing';
import { contractIsEnergyManagement } from '../Contracts/ManageContracts/EnergyManagement';
import { getCurrent15min } from '../../utils/consumption';

const LandingPageLifts: React.FunctionComponent = () => {
  const { users, invoices, contracts, selectedMeteringPoint, selectedMeteringPointContract, landingPage } = OmaLummeApp.useState();

  const [latestInvoice, setLatestInvoice] = React.useState<InvoiceAkamon>();

  const inclVat = users.activeUser.customerType?.toLowerCase() === 'business' ? false : true;

  const { t } = useTranslation();

  const spotData = landingPage.spotPricesToday;
  const current15min = getCurrent15min();
  const currentHour = spotData?.find((h) => isSameMinute(new Date(h.period.start), new Date(current15min)));
  const currentHourValue = currentHour
    ? setPriceFormat(roundNumber((inclVat ? currentHour.meanPriceWithVat : currentHour.meanPriceWithoutVat) / 10))
    : undefined;

  React.useEffect(() => {
    if (invoices && invoices[0]) {
      const filterOutUndefined = (value: Date | undefined): value is Date => {
        return value !== undefined;
      };
      const invoiceDates = invoices.map((invoice): Date => {
        return new Date(invoice.dateOfInvoice);
      });
      const filteredInvoiceDates = invoiceDates.filter(filterOutUndefined);
      const mostRecentInvoiceDate = new Date(Math.max(...filteredInvoiceDates.map((d) => d.getTime())));

      const latestInvoice = invoices.find((i) => new Date(i.dateOfInvoice).getTime() === mostRecentInvoiceDate.getTime());

      setLatestInvoice(latestInvoice);
    } else {
      setLatestInvoice(undefined);
    }
  }, [selectedMeteringPoint, invoices]);

  return (
    <div>
      <div className={styles.liftups}>
        <PriceFixLift />
        <EVChargerLift />
        <ConsumptionLift />
        {spotData !== undefined && spotData.length > 0 && currentHourValue && (
          <Liftup link="/nordpool" className="lavender">
            <h4>{t('consumption:consumption.spotPriceLiftTitle')}</h4>
            <span className={styles.sans}>
              <Trans
                i18nKey="consumption:consumption.spotPriceLiftNow"
                values={{
                  from: format(new Date(currentHour?.period.start || new Date()), 'HH:mm'),
                  to: format(new Date(currentHour?.period.end || new Date()), 'HH:mm'),
                }}
              >
                . <span></span>
              </Trans>
            </span>
            <h3 className="h2">
              {currentHourValue}&nbsp;<span>{t('consumption:nordPoolGraph.spotUnit')}</span>
            </h3>
            <span className={styles.sans}>{t('consumption:consumption.spotPriceLiftTodayAvg')}</span>
            <h3 className="h2">
              {setPriceFormat(
                roundNumber(spotData.reduce((a, b) => a + b[inclVat ? 'meanPriceWithVat' : 'meanPriceWithoutVat'], 0) / spotData.length / 10),
              )}
              &nbsp;<span>{t('consumption:nordPoolGraph.spotUnit')}</span>
            </h3>
          </Liftup>
        )}
        {latestInvoice && (
          <Liftup link="/billing" className="green">
            <h4>{t('consumption:consumption.lastMonthConsumption')}</h4>
            <span className="h1">{`${setPriceFormat(roundNumber(latestInvoice.totalWithVat))} €`}</span>
            <span className={styles.sans}>{`${t(`billing:billing.dueDate`)} ${formatDate(latestInvoice.dueDate)}`}</span>
          </Liftup>
        )}
        {localStorage.getItem('EnergyManagementBannerDisplayed') === 'true' &&
          selectedMeteringPoint &&
          ((contracts && contracts.filter((c) => contractIsEnergyManagement(c)).length > 0) ||
            (selectedMeteringPointContract && contractIsEnergyManagement(selectedMeteringPointContract))) && (
            <Liftup link="/energyManagement" className="turquoise">
              <h4>{t('landingPage:energyManagementBanner.title')}</h4>
              <span className={styles.sans}>{t(`landingPage:energyManagementBanner.liftDescription`)}</span>
            </Liftup>
          )}
      </div>
    </div>
  );
};

export default LandingPageLifts;
